import React, { Suspense } from 'react';
import { Route, Routes } from 'react-router-dom';

import Layout from 'components/Layout';
import PageContainer from 'components/PageContainer';
import FullScreenLoader from 'components/FullScreenLoader';
import { GuardedRoute } from './GuardedRoute';
import { HomeRoute } from './HomeRoute';
import { RoleGuardedRoute } from './RoleGuardedRoute';
import { AdminLayout } from 'features/admin/AdminLayout';

const Contacts = React.lazy(() => import('features/contacts/pages/Contacts'));
const EditContact = React.lazy(() => import('features/contacts/pages/EditContact'));

const Leads = React.lazy(() => import('features/leads/pages/Leads'));

const Companies = React.lazy(() => import('features/companies/pages/Companies'));
const EditCompany = React.lazy(() => import('features/companies/pages/EditCompany'));

const Orders = React.lazy(() => import('features/orders/pages/Orders'));
const EditOrder = React.lazy(() => import('features/orders/pages/EditOrder'));
const InvoiceView = React.lazy(() => import('features/orders/pages/InvoiceView'));

const Employees = React.lazy(() => import('features/employees/pages/Employees'));
const EditEmployee = React.lazy(() => import('features/employees/pages/EditEmployee'));
const EmployeePlan = React.lazy(() => import('features/employees/pages/EmployeePlan'));

const Calendar = React.lazy(() => import('features/bookings/pages/Calendar'));
const Bookings = React.lazy(() => import('features/bookings/pages/Bookings'));
const EditBooking = React.lazy(() => import('features/bookings/pages/EditBookings'));

const Services = React.lazy(() => import('features/services/pages/Services'));
const EditService = React.lazy(() => import('features/services/pages/EditService'));

const Settings = React.lazy(() => import('features/settings/pages/Settings'));

const Statistics = React.lazy(() => import('features/statistics/pages/Statistics'));
const ErganiEmployees = React.lazy(() => import('features/ergani/pages/ErganiEmployees'));
const AdminTenants = React.lazy(() => import('features/admin/tenants/AdminTenants').then(module => ({ default: module.AdminTenants })));
const AdminTenantDetail = React.lazy(() =>
  import('features/admin/tenants/AdminTenantDetail').then(module => ({ default: module.AdminTenantDetail })),
);

export const CONTACT_PATH: string = '/contacts';
export const LEAD_PATH: string = '/leads';
export const COMPANY_PATH: string = '/companies';
export const ORDER_PATH: string = '/orders';
export const EMPLOYEE_PATH: string = '/employees';
export const BOOKING_PATH: string = '/bookings';
export const SERVICE_PATH: string = '/services';
export const STATISTICS_PATH: string = '/statistics';
export const SETTING_PATH: string = '/settings';
export const ERGANI_PATH: string = '/ergani';
export const ADMIN_PATH: string = '/admin';

const AppRoutes = () => (
  <Routes>
    <Route path="/" element={<Layout />}>
      {/* All other routes use PageContainer for max-width centering */}
      <Route element={<PageContainer />}>
        <Route path="/" element={<HomeRoute />} />
        {/* Contacts */}
        <Route path={CONTACT_PATH} element={<Contacts />} />
        <Route path={`${CONTACT_PATH}/:id`} element={<EditContact />} />
        {/* Leads */}
        <Route path={LEAD_PATH} element={<GuardedRoute requiredPermissions={['leads:read']} element={<Leads />} />} />
        {/* Companies */}
        <Route path={COMPANY_PATH} element={<GuardedRoute requiredPermissions={['companies:read']} element={<Companies />} />} />
        <Route path={`${COMPANY_PATH}/:id`} element={<GuardedRoute requiredPermissions={['companies:read']} element={<EditCompany />} />} />
        {/* Orders */}
        <Route path={ORDER_PATH} element={<Orders />} />
        <Route path={`${ORDER_PATH}/:id`} element={<EditOrder />} />
        {/* Employees */}
        <Route path={EMPLOYEE_PATH} element={<Employees />} />
        <Route path={`${EMPLOYEE_PATH}/:id`} element={<EditEmployee />} />
        <Route path={`${EMPLOYEE_PATH}/:id/schedule/:scheduleId`} element={<EmployeePlan />} />
        {/* Bookings */}
        <Route path={BOOKING_PATH} element={<Calendar />} />
        <Route path={`${BOOKING_PATH}/list`} element={<Bookings />} />
        <Route path={`${BOOKING_PATH}/:id`} element={<EditBooking />} />
        {/* Services */}
        <Route path={SERVICE_PATH} element={<Services />} />
        <Route path={`${SERVICE_PATH}/:id`} element={<EditService />} />
        {/* Statistics */}
        <Route path={STATISTICS_PATH} element={<GuardedRoute requiredPermissions={['statistics:read']} element={<Statistics />} />} />
        {/* Ergani */}
        <Route path={ERGANI_PATH} element={<GuardedRoute requiredPermissions={['ergani:access']} element={<ErganiEmployees />} />} />
      </Route>
      {/* Settings */}
      <Route path={SETTING_PATH} element={<Settings />} />
      <Route path={ADMIN_PATH} element={<RoleGuardedRoute role="platform-admin" element={<AdminLayout />} />}>
        <Route path="tenants" element={<AdminTenants />} />
        <Route path="tenants/:alias" element={<AdminTenantDetail />} />
      </Route>
    </Route>
    {/* Invoice print view — bare page, no app chrome, so it prints cleanly */}
    <Route
      path={`${ORDER_PATH}/:orderId/invoices/:invoiceId`}
      element={
        <Suspense fallback={<FullScreenLoader />}>
          <GuardedRoute requiredPermissions={['invoicing:read']} element={<InvoiceView />} />
        </Suspense>
      }
    />
  </Routes>
);

export default AppRoutes;
